// Static metadata for each prerequisite a logbook can be missing. Pure data so
// adding/removing a gated requirement is a data change, not a UI change. The
// backend owns whether a requirement is met (the `missing` keys); the frontend
// owns how to fix it — the copy (i18n) and the route of the form that fills it.

export interface RequirementMeta {
  /** Machine key, matches the backend `missing` entries. */
  readonly key: string;
  /** Decorative emoji for the checklist row; not translatable. */
  readonly emoji: string;
  /** Route of the form that satisfies this requirement. */
  readonly to: (lng: string, stationId: string) => string;
}

/**
 * Order the setup steps appear in the floating guide. This first onboarding gets
 * the station operating for fuel reception: corporate data first (the business
 * name, RFC and CRE permit feed every official PDF), then an authorized signature
 * (the official PDF must name at least one authorized signer), then the layout by
 * dependency — an area before the tank that lives in it.
 *
 * Wells are deliberately NOT here: they are only needed for well monitoring, which
 * has its own just-in-time gate (`ModuleSetupGate moduleId="monitoring"`) that
 * asks for them when the user opens an inspection. `well` stays in REQUIREMENTS so
 * that gate can still render it.
 */
export const SETUP_STEP_ORDER: readonly string[] = [
  'compliance_profile',
  'authorized_signature',
  'area',
  'tank'
];

export const REQUIREMENTS: Record<string, RequirementMeta> = {
  authorized_signature: {
    key: 'authorized_signature',
    emoji: '✍️',
    to: (lng) => `/${lng}/settings/profile`
  },
  area: {
    key: 'area',
    emoji: '🗺️',
    to: (lng, stationId) => `/${lng}/stations/${stationId}/areas/new`
  },
  tank: {
    key: 'tank',
    emoji: '🛢️',
    to: (lng, stationId) => `/${lng}/stations/${stationId}/tanks/new`
  },
  well: {
    key: 'well',
    emoji: '🕳️',
    to: (lng) => `/${lng}/monitoring/wells/new`
  },
  compliance_profile: {
    key: 'compliance_profile',
    emoji: '📋',
    to: (lng, stationId) => `/${lng}/stations/${stationId}/compliance-profile`
  }
};
